/**
 * src/pages/WhatIsConsciousness/sections/IdentityContinuity.tsx
 * "Ship of Theseus for your brain" thought experiment slider (0-100% neuron replacement)
 * comparing Psychological Continuity, Biological Animalism, and Pattern View,
 * plus evidence-labelled clinical case facts (split-brain, anaesthesia, sleep).
 */

import React, { useState } from "react";
import { Sliders, Cpu, Activity, Sparkles, Scale, Info } from "lucide-react";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import "@/components/cosmic/cosmic.css";
export function IdentityContinuity() {
  const [replacementPct, setReplacementPct] = useState<number>(50);

  // Dynamic philosophical reflections based on replacement percentage
  const getStagePrompt = (pct: number) => {
    if (pct === 0) {
      return {
        stage: "100% Biological Architecture",
        desc: "Every single one of your roughly 86 billion neurons is biological, metabolizing glucose, firing neurotransmitters, and vulnerable to hypoxia.",
        reflection: "Unquestionably the biological organism you were born as."
      };
    }
    if (pct < 30) {
      return {
        stage: "Minor Prosthetic Augmentation",
        desc: `${pct}% of your biological neurons have been replaced one by one with nanotech silicon circuits mimicking their input/output functions identically.`,
        reflection: "Similar to a modern cochlear implant or retinal prosthesis—most intuitions agree your subjective stream continues uninterrupted."
      };
    }
    if (pct < 70) {
      return {
        stage: "The Ambiguous Threshold",
        desc: `${pct}% of your brain is now synthetic computational hardware. Organic neurons continue to synapse directly with microscopic silicon gates.`,
        reflection: "You report no difference from the inside. But are you slowly fading into a synthetic mimic, or is identity preserved across continuous function?"
      };
    }
    if (pct < 100) {
      return {
        stage: "Predominantly Synthetic Mind",
        desc: `${pct}% synthetic. Only isolated biological clusters remain in deep cortical and subcortical crevices.`,
        reflection: "Biological animalists argue the original biological organism is dying. Pattern theorists maintain that since your memories and habits persist, so do you."
      };
    }
    return {
      stage: "100% Synthetic Brain (Ship of Theseus Complete)",
      desc: "Not a single biological neuron remains. Every calculation is performed by neuromorphic hardware executing the exact dynamical patterns of your former brain.",
      reflection: "Did you survive the replacement, or did you die somewhere along the slider and leave an unconscious philosophical zombie in your place?"
    };
  };
  const currentPrompt = getStagePrompt(replacementPct);
  return <section className="w-full max-w-full min-w-0" aria-labelledby="identity-continuity-heading">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="size-1.5 rounded-[1px] bg-primary rotate-45 shadow-[0_0_8px_1px_var(--glow-primary)] shrink-0" aria-hidden="true" />
            <span className="ql-eyebrow">Personal Identity & The Self</span>
          </div>
          <h2 id="identity-continuity-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            The Ship of Theseus for Your Brain
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <EvidenceBadge level="philosophy" />
          <Footnote n={9} />
        </div>
      </div>

      {/* Main Interactive Slider Card */}
      <div className="ql-glass p-6 sm:p-8 w-full max-w-full min-w-0 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/30 pb-4 mb-6">
          <div className="flex items-center gap-2">
            <Cpu className="size-5 text-primary" />
            <span className="font-serif text-lg font-bold text-foreground">
              Gradual Neuron Replacement Simulator
            </span>
          </div>
          <span className="font-mono text-sm font-bold text-primary px-3 py-1 rounded-full bg-primary/10 border border-primary/30">
            {replacementPct}% Synthetic Hardware
          </span>
        </div>

        {/* Range Slider Control */}
        <div className="space-y-4 mb-8">
          <div className="flex justify-between text-xs font-mono text-muted-foreground">
            <span>0% (Pure Biology)</span>
            <span>50% (Bionic Hybrid)</span>
            <span>100% (Complete Synthetic)</span>
          </div>

          <div className="relative flex items-center">
            <input type="range" min={0} max={100} step={1} value={replacementPct} onChange={e => setReplacementPct(Number(e.target.value))} className="ob-timeline-slider w-full" aria-label="Percentage of biological neurons replaced with artificial replicas" />
          </div>
        </div>

        {/* Dynamic Thought Experiment Prompt */}
        <div className="p-5 sm:p-6 rounded-xl border border-primary/30 bg-primary/5 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="font-mono text-xs uppercase tracking-wider text-primary font-bold">
              {currentPrompt.stage}
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              Thought Experiment: Continuity of Consciousness
            </span>
          </div>
          <p className="font-sans text-sm sm:text-base text-foreground leading-relaxed break-words">
            {currentPrompt.desc}
          </p>
          <div className="pt-2 border-t border-border/30 text-xs sm:text-sm text-muted-foreground font-sans italic break-words">
            "{currentPrompt.reflection}"
          </div>
        </div>

        {/* Three Philosophical Stances */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="p-4 rounded-lg bg-card/40 border border-border/40">
            <div className="flex items-center gap-2 mb-2 font-mono text-xs text-primary font-bold uppercase">
              <Scale className="size-3.5" />
              <span>Psychological Continuity</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
              (Locke, Parfit): You survive as long as your memories, beliefs, personality traits, and stream of
              consciousness continue smoothly. The underlying hardware (carbon vs. silicon) does not matter.
            </p>
          </div>

          <div className="p-4 rounded-lg bg-card/40 border border-border/40">
            <div className="flex items-center gap-2 mb-2 font-mono text-xs text-primary font-bold uppercase">
              <Activity className="size-3.5" />
              <span>Biological Animalism</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
              (Olson): You are fundamentally a biological animal organism. Replacing your biological brain with
              silicon chips kills the organism, even if the resulting computer speaks and behaves identically to you.
            </p>
          </div>

          <div className="p-4 rounded-lg bg-card/40 border border-border/40">
            <div className="flex items-center gap-2 mb-2 font-mono text-xs text-primary font-bold uppercase">
              <Sparkles className="size-3.5" />
              <span>Pattern / Illusion View</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
              (Buddhism, Hofstadter, Dennett): There is no persistent underlying "ego" to preserve in the first place.
              The self is an ongoing computational whirlpool. Any medium that sustains the pattern sustains the process.
            </p>
          </div>
        </div>
      </div>

      {/* Clinical Evidence Cases: Split-Brain & Anaesthesia */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Info className="size-4 text-primary" />
          <h3 className="font-serif text-lg font-bold text-foreground">
            Empirical Clues from Clinical Neurology
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="ql-card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-serif text-sm font-bold text-foreground">
                Split-Brain Patients
              </span>
              <EvidenceBadge level="established" compact />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
              Severing the corpus callosum (to treat severe epilepsy) results in two functionally independent
              hemispheres with separate visual fields and intentions. Roger Sperry and Michael Gazzaniga showed
              that the unified self can be surgically divided into two co-existing streams of awareness.
            </p>
          </div>

          <div className="ql-card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-serif text-sm font-bold text-foreground">
                Reversible Anaesthesia
              </span>
              <EvidenceBadge level="established" compact />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
              General anaesthetics (like propofol or isoflurane) reversibly extinguish subjective awareness without
              killing the brain. Upon waking, patients report no sensation of elapsed time—identity reconstitutes
              seamlessly as cortical integration resumes.
            </p>
          </div>

          <div className="ql-card p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-serif text-sm font-bold text-foreground">
                Deep Sleep & Dreaming
              </span>
              <EvidenceBadge level="established" compact />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
              Every night in non-REM slow-wave sleep, your conscious self dissolves for hours at a time, punctuated
              by hallucinatory dream states where the prefrontal monitor is turned down. You die and are reborn
              conceptually every single night.
            </p>
          </div>
        </div>
      </div>
    </section>;
}